import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { verweisKomponente } from '@/lib/assistent/assistent-beilagen'

/**
 * AP-18 5.1 — die Antwort des Assistenten mit ihren **Verweisen**.
 *
 * Das Modell schreibt `[Text](Ziel)`; das Ziel nimmt es aus einem Werkzeugergebnis. Aufgelöst wird
 * hier nur, was unten steht — jede Art führt auf eine Seite der Anwendung. Ein Ziel, das keine Art
 * ist (eine Internetadresse, ein erfundenes), wird **kein** Verweis: Es bleibt sein Text.
 *
 * Eine neue Art kommt mit dem Werkzeug, das sie liefert.
 */
const KENNUNG = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const VERWEIS_ARTEN: Record<string, (ziel: string) => string | null> = {
  // `hilfe:<bereich>/<thema>#<anker>` → Abschnitt des Handbuchs
  hilfe: (ziel) => (/^[a-z0-9-]+\/[a-z0-9-]+(#[^\s()]*)?$/i.test(ziel) ? `/hilfe/${ziel}` : null),
  // `seite:/pfad` → eine Seite der Anwendung
  seite: (ziel) => (/^\/(?!\/)[^\s()]*$/.test(ziel) ? ziel : null),
  // Datensätze: `art:<kennung>` → ihre Seite (Phase C)
  partner: (ziel) => (KENNUNG.test(ziel) ? `/clients/${ziel}` : null),
  beleg: (ziel) => (KENNUNG.test(ziel) ? `/correspondence/${ziel}` : null),
  projekt: (ziel) => (KENNUNG.test(ziel) ? `/projects/${ziel}` : null),
  kalkulation: (ziel) => (KENNUNG.test(ziel) ? `/calculations/${ziel}` : null),
  artikel: (ziel) => (KENNUNG.test(ziel) ? `/articles/${ziel}` : null),
}

/** Der Pfad zu einem Ziel — `null`, wenn es keine bekannte Art ist. */
export function verweisPfad(ziel: string): string | null {
  const trenner = ziel.indexOf(':')
  if (trenner < 1) return null
  return VERWEIS_ARTEN[ziel.slice(0, trenner)]?.(ziel.slice(trenner + 1)) ?? null
}

const MUSTER = /\[([^\]\n]+)\]\(([^)\s]+)\)|\*\*([^*\n]+)\*\*/g

export function Antworttext({ text }: { text: string }) {
  const teile: ReactNode[] = []
  let rest = 0
  for (const t of text.matchAll(MUSTER)) {
    const [ganz, beschriftung, ziel, fett] = t
    const ab = t.index ?? 0
    teile.push(text.slice(rest, ab))
    if (fett !== undefined) teile.push(<strong key={ab}>{fett}</strong>)
    else {
      // Ein Fachmodul zeichnet seine Art selbst (eine Fundstelle öffnet einen Dialog, keine Seite).
      const trenner = ziel.indexOf(':')
      const Eigene = trenner > 0 ? verweisKomponente(ziel.slice(0, trenner)) : undefined
      if (Eigene) {
        teile.push(
          <Eigene key={ab} ziel={ziel.slice(trenner + 1)}>
            {beschriftung}
          </Eigene>,
        )
        rest = ab + ganz.length
        continue
      }
      const pfad = verweisPfad(ziel)
      teile.push(
        pfad ? (
          <Link key={ab} to={pfad} className="text-primary hover:underline">
            {beschriftung}
          </Link>
        ) : (
          beschriftung
        ),
      )
    }
    rest = ab + ganz.length
  }
  teile.push(text.slice(rest))
  return <>{teile}</>
}
